import React, { useMemo } from 'react';
import SectionHeader from './SectionHeader';
import useInView from '../hooks/useInView';

// Rendered like a colophon: quiet mono rows, counts = number of projects
// (from projects.json) whose tags/description mention the technology.
const STACK_GROUPS = [
  {
    id: 'languages',
    label: { ja: 'LANGUAGES', en: 'LANGUAGES' },
    items: [
      { label: 'JavaScript', keywords: ['javascript'] },
      { label: 'TypeScript', keywords: ['typescript'] },
      { label: 'Python', keywords: ['python'] },
      { label: 'Java', keywords: ['java ', 'javafx', '"java"'] },
      { label: 'C', keywords: ['c言語', '"c"'] },
      { label: 'x86 Assembly', keywords: ['x86'] },
    ],
  },
  {
    id: 'frontend',
    label: { ja: 'FRONTEND / UX', en: 'FRONTEND / UX' },
    items: [
      { label: 'React', keywords: ['react'] },
      { label: 'Next.js', keywords: ['next.js'] },
      { label: 'HTML', keywords: ['"html"'] },
      { label: 'CSS', keywords: ['"css"'] },
      { label: 'Tailwind CSS', keywords: ['tailwind'] },
      { label: 'Bootstrap', keywords: ['bootstrap'] },
      { label: 'Material-UI', keywords: ['material-ui'] },
      { label: 'WebRTC', keywords: ['webrtc'] },
      { label: 'Figma', keywords: ['figma'] },
    ],
  },
  {
    id: 'backend',
    label: { ja: 'BACKEND / DATA', en: 'BACKEND / DATA' },
    items: [
      { label: 'Express', keywords: ['express'] },
      { label: 'Firebase', keywords: ['firebase', 'firestore'] },
      { label: 'MongoDB', keywords: ['mongodb', 'mongoose'] },
      { label: 'PostgreSQL', keywords: ['postgresql'] },
      { label: 'Stripe', keywords: ['stripe'] },
      { label: 'OpenAI Realtime API', keywords: ['openai'] },
    ],
  },
  {
    id: 'cloud',
    label: { ja: 'CLOUD / OPS', en: 'CLOUD / OPS' },
    items: [
      { label: 'AWS Elastic Beanstalk', keywords: ['elastic beanstalk'] },
      { label: 'Amazon Cognito', keywords: ['cognito'] },
      { label: 'ALB', keywords: ['alb'] },
      { label: 'Firebase Hosting / Functions', keywords: ['firebase hosting', 'functions', 'web.app'] },
      { label: 'Render', keywords: ['render'] },
      { label: 'Linux', keywords: ['linux'] },
    ],
  },
];

function toCorpus(project) {
  return [
    ...(project.tags_en || []),
    ...(project.tags_ja || []),
    project.description_en || '',
    project.description_ja || '',
    ...(project.links || []).map((link) => `${link.url || ''} ${link.title_en || ''} ${link.title_ja || ''}`),
  ]
    .join(' ')
    .toLowerCase();
}

function countProjects(projectCorpus, keywords, exactTags) {
  return projectCorpus.reduce((count, corpus, index) => {
    const used = keywords.some((keyword) => {
      const normalized = keyword.toLowerCase();
      // Quoted keywords ("java") match whole tags only, so e.g. "java" does
      // not count JavaScript projects.
      if (normalized.startsWith('"') && normalized.endsWith('"')) {
        return exactTags[index].has(normalized.slice(1, -1));
      }
      return corpus.includes(normalized);
    });
    return used ? count + 1 : count;
  }, 0);
}

function StackSection({ projects, language }) {
  const [bodyRef, isVisible] = useInView();

  const projectCorpus = useMemo(() => projects.map((project) => toCorpus(project)), [projects]);
  const exactTags = useMemo(
    () =>
      projects.map(
        (project) =>
          new Set(
            [...(project.tags_en || []), ...(project.tags_ja || [])].map((tag) =>
              String(tag).trim().toLowerCase()
            )
          )
      ),
    [projects]
  );

  const groups = useMemo(
    () =>
      STACK_GROUPS.map((group) => ({
        ...group,
        items: group.items
          .map((item) => ({
            ...item,
            count: countProjects(projectCorpus, item.keywords, exactTags),
          }))
          .filter((item) => item.count > 0)
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      })).filter((group) => group.items.length > 0),
    [exactTags, projectCorpus]
  );

  const copy =
    language === 'ja'
      ? {
          label: 'STACK',
          title: 'スタック',
          lead: `×n は、その技術を使ったプロジェクトの数です（全${projects.length}件）。`,
        }
      : {
          label: 'STACK',
          title: 'Stack',
          lead: `×n = number of projects using the technology (of ${projects.length} total).`,
        };

  return (
    <section id="stack" className="stack-section" aria-label={copy.title}>
      <SectionHeader index="08" label={copy.label} title={copy.title} lead={copy.lead} />

      <div ref={bodyRef} className={`stack-colophon reveal ${isVisible ? 'is-visible' : ''}`}>
        {groups.map((group) => (
          <div key={group.id} className="stack-row">
            <p className="stack-row__label mono">{group.label[language]}</p>
            <p className="stack-row__items">
              {group.items.map((item, index) => (
                <span key={item.label} className="stack-item">
                  <span className="stack-item__name">{item.label}</span>
                  <span className="stack-item__count mono">×{item.count}</span>
                  {index < group.items.length - 1 ? (
                    <span className="stack-item__divider" aria-hidden="true">
                      ·
                    </span>
                  ) : null}
                </span>
              ))}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default StackSection;
